/**
 * The Vera Dataverse access matrix: which security role gets which privileges
 * on which table. SINGLE SOURCE OF TRUTH, consumed by:
 *   - packages/ops/scripts/provision-vera-security-roles.ts (creates roles and
 *     tops up privileges; add-only)
 *   - the /admin Access Health tab (compares each role's actually-attached
 *     privileges against this matrix and reports gaps)
 *
 * Editing rules:
 *   - Every vba_* table in TABLES must have a row here; the provision script
 *     fails until a new table gets an explicit per-role decision.
 *   - Dataverse requires AppendTo on the TARGET table to set a lookup. When an
 *     Ops-owned table gains a lookup to a reference table, upgrade that
 *     reference table's ops bundle to 'raa' (found the hard way 2026-07-02:
 *     "missing prvAppendTovba_ServiceOption" for Ops.User).
 *   - Changes here do nothing until the provision script runs per environment.
 */

export type PrivilegeBundle = 'read' | 'crud' | 'cruda' | 'raa' | 'rwaa' | 'craa' | 'crwaa';
export type RoleKey = 'admin' | 'ops' | 'catalog' | 'sales' | 'marketing';
export type PrivilegeAction = 'Create' | 'Read' | 'Write' | 'Delete' | 'Append' | 'AppendTo';

export interface TableAccess {
  /** Singular logical name of the entity (used to construct privilege names). */
  logicalName: string;
  admin?: PrivilegeBundle;
  ops?: PrivilegeBundle;
  catalog?: PrivilegeBundle;
  sales?: PrivilegeBundle;
  marketing?: PrivilegeBundle;
}

// Custom (vba_*) tables. Decisions follow docs/vera-entra-app-roles-setup.md Part 3.
export const VBA_ACCESS: TableAccess[] = [
  // ── Catalog / config (Catalog manages, Ops reads as reference data) ──
  // Ops gets 'raa' (Read/Append/AppendTo), not bare 'read', on the reference
  // tables that Ops-owned rows point at via lookups (see editing rules above).
  { logicalName: 'vba_managedlist',           admin: 'cruda', catalog: 'cruda', ops: 'raa' },
  // Ops also CREATES list items (the A5a "Other" inline-add on option dropdowns).
  { logicalName: 'vba_managedlistitem',       admin: 'cruda', catalog: 'cruda', ops: 'craa' },
  { logicalName: 'vba_gicategory',            admin: 'cruda', catalog: 'cruda', ops: 'raa' },
  { logicalName: 'vba_gifield',               admin: 'cruda', catalog: 'cruda', ops: 'raa' },
  { logicalName: 'vba_solution',              admin: 'cruda', catalog: 'cruda', ops: 'raa' },
  { logicalName: 'vba_servicegroup',          admin: 'cruda', catalog: 'cruda', ops: 'read' },
  { logicalName: 'vba_service',               admin: 'cruda', catalog: 'cruda', ops: 'raa' },
  { logicalName: 'vba_servicelistrule',       admin: 'cruda', catalog: 'cruda', ops: 'read' },
  { logicalName: 'vba_serviceoption',         admin: 'cruda', catalog: 'cruda', ops: 'raa' },
  { logicalName: 'vba_partner',               admin: 'cruda', catalog: 'cruda', ops: 'read' },
  { logicalName: 'vba_partnergroupref',       admin: 'cruda', catalog: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_partnerplanref',        admin: 'cruda', catalog: 'cruda', ops: 'cruda' },
  // Plan-design dictionary: Catalog curates it, and an Ops reviewer can add a
  // category/field inline from the extraction review panel, so Ops gets Create.
  // AppendTo is required on both: a field binds its Category, a value binds its Field.
  { logicalName: 'vba_plandesigncategory',    admin: 'cruda', catalog: 'cruda', ops: 'craa' },
  { logicalName: 'vba_plandesignfield',       admin: 'cruda', catalog: 'cruda', ops: 'craa' },
  // S/S/O placements of a plan-design field/category: Catalog-authored, Ops reads.
  { logicalName: 'vba_plandesignssodisplay',  admin: 'cruda', catalog: 'cruda', ops: 'read' },
  // Implementation templates (Catalog manages)
  { logicalName: 'vba_imptemplate',           admin: 'cruda', catalog: 'cruda', ops: 'read' },
  { logicalName: 'vba_imptemplatesection',    admin: 'cruda', catalog: 'cruda', ops: 'read' },
  { logicalName: 'vba_imptemplatetask',       admin: 'cruda', catalog: 'cruda', ops: 'read' },
  { logicalName: 'vba_imptemplatedep',        admin: 'cruda', catalog: 'cruda', ops: 'read' },
  { logicalName: 'vba_imptemplatesectionref', admin: 'cruda', catalog: 'cruda', ops: 'read' },
  { logicalName: 'vba_impsolutionmodule',     admin: 'cruda', catalog: 'cruda', ops: 'read' },
  // Email templates — Catalog manages, Ops uses
  { logicalName: 'vba_emailtemplate',         admin: 'cruda', catalog: 'cruda', ops: 'read' },
  // AdminConfig — global config, read by all signed-in users
  { logicalName: 'vba_adminconfig',           admin: 'cruda', catalog: 'cruda', ops: 'read', sales: 'read' },

  // ── Ops-owned ──
  // RBAC hardening Phase 2 (2026-07-10): opsgroup + contractyear deletes run
  // ONLY through the BFF destructive endpoints (service identity), so the
  // Ops user role keeps everything EXCEPT Delete on these two tables
  // ('crwaa'). Admin keeps cruda for repair scripts. The strip script
  // (schema:strip-ops-destructive-privileges) enforces this on the live role;
  // the provision script is add-only and will not re-grant Delete.
  { logicalName: 'vba_opsgroup',              admin: 'cruda', ops: 'crwaa' },
  { logicalName: 'vba_contractyear',          admin: 'cruda', ops: 'crwaa' },
  { logicalName: 'vba_groupsolution',         admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_groupservice',          admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_groupserviceoption',    admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_groupvendor',           admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_groupcontactrole',      admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_groupteammate',         admin: 'cruda', ops: 'cruda' },
  // Plan design per group. Renew clones the plan rows and the Plan Design tab
  // writes values; both run on the signed-in Ops user's token. These five
  // tables were added to TABLES in impform B2 but never categorized here,
  // which is why renew died on prvReadvba_groupplan for an Ops user (2026-09-08).
  { logicalName: 'vba_groupplan',             admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_plandesignvalue',       admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_giinstance',            admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_renewal',               admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_salesquestionnaire',    admin: 'cruda', ops: 'cruda', sales: 'read' },
  { logicalName: 'vba_groupmonthlymetric',    admin: 'cruda', ops: 'cruda', sales: 'read' },
  { logicalName: 'vba_savedview',             admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_geocode',               admin: 'cruda', ops: 'cruda', catalog: 'read' },
  // Operation Daylight (goals/quests + AE team cards, edited in-app by Ops)
  { logicalName: 'vba_daylightgoal',          admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_daylightteam',          admin: 'cruda', ops: 'cruda' },
  // Implementation projects (Ops-side instances of the templates)
  { logicalName: 'vba_impproject',            admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_impprojectsection',     admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_impprojecttask',        admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_impprojectdep',         admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_impprojectnote',        admin: 'cruda', ops: 'cruda' },
  { logicalName: 'vba_impprojectdoc',         admin: 'cruda', ops: 'cruda' },

  // ── Personalization / telemetry / role mgmt — every signed-in user ──
  { logicalName: 'vba_userpreference',        admin: 'cruda', ops: 'crud', catalog: 'crud', sales: 'crud' },
  { logicalName: 'vba_useractivity',          admin: 'cruda', ops: 'crud', catalog: 'crud', sales: 'crud' },
  // userroleassignment: Admin writes; everyone else reads (for own-role check)
  { logicalName: 'vba_userroleassignment',    admin: 'cruda', ops: 'read', catalog: 'read', sales: 'read' },
  // Braze sync log — written by the braze-sync Function (its own app user),
  // read by the Marketing dashboard. Marketing gets read-only; the dashboard
  // touches no other table, so the Marketing role stays tightly scoped.
  { logicalName: 'vba_brazesynclog',          admin: 'cruda', marketing: 'read' },
  { logicalName: 'vba_brazesyncrun',          admin: 'cruda', marketing: 'read' },
];

// Standard CRM tables. Read across the board; Ops gets CRUD+Append on
// annotation/task because notes/activities are its primary collaboration surface.
export const CRM_ACCESS: TableAccess[] = [
  // Ops rows bind lookups to these CRM records (opsgroup/giinstance → account,
  // groupcontactrole → contact, intake/renewal → opportunity, contract-year /
  // imp-task assignee → systemuser), which needs AppendTo on the target.
  { logicalName: 'account',            admin: 'read', ops: 'raa',   catalog: 'read', sales: 'read' },
  { logicalName: 'contact',            admin: 'read', ops: 'raa',   catalog: 'read', sales: 'read' },
  { logicalName: 'opportunity',        admin: 'read', ops: 'raa',                    sales: 'read' },
  { logicalName: 'opportunityproduct', admin: 'read', ops: 'read',                   sales: 'read' },
  { logicalName: 'activitypointer',    admin: 'read', ops: 'read',                   sales: 'read' },
  { logicalName: 'annotation',         admin: 'read', ops: 'cruda' },
  { logicalName: 'task',               admin: 'read', ops: 'cruda' },
  // Team management (the /admin Access Health check + role→team sync): the
  // Vera Admin role needs to read users/teams and add users to the Vera
  // teams. Write on team is required by Dataverse to modify owner-team
  // membership; deliberately no Create/Delete on either table.
  { logicalName: 'team',               admin: 'rwaa' },
  { logicalName: 'systemuser',         admin: 'raa', ops: 'raa' },
  // Access Health's role-privilege check reads security roles
  // (RetrieveRolePrivilegesRole needs prvReadRole). The privilege catalog
  // itself has no read privilege — it's org-readable.
  { logicalName: 'role',               admin: 'read' },
];

export const ALL_ACCESS: TableAccess[] = [...VBA_ACCESS, ...CRM_ACCESS];

export interface VeraRoleConfig {
  key: RoleKey;
  name: string;
  description: string;
}

export const VERA_DATAVERSE_ROLES: VeraRoleConfig[] = [
  {
    key: 'admin',
    name: 'Vera Admin',
    description:
      'Full CRUD on all Vera (vba_*) tables; read on core CRM. Used by Vera Admins Team.',
  },
  {
    key: 'ops',
    name: 'Vera Ops User',
    description:
      'Full CRUD on Ops-owned tables; read on catalog reference data; read on core CRM. Used by Vera Ops Team.',
  },
  {
    key: 'catalog',
    name: 'Vera Catalog User',
    description: 'Full CRUD on catalog/config tables. Used by Vera Catalog Team.',
  },
  {
    key: 'sales',
    name: 'Vera Sales User',
    description:
      'Read-only on AdminConfig + reporting tables + core CRM. Used by Vera Sales Team.',
  },
  {
    key: 'marketing',
    name: 'Vera Marketing User',
    description:
      'Read-only on the Braze sync log (vba_brazesynclog). Used by Vera Marketing Team.',
  },
];

export function actionsForBundle(bundle: PrivilegeBundle): PrivilegeAction[] {
  if (bundle === 'read') return ['Read'];
  if (bundle === 'raa') return ['Read', 'Append', 'AppendTo'];
  if (bundle === 'rwaa') return ['Read', 'Write', 'Append', 'AppendTo'];
  if (bundle === 'craa') return ['Create', 'Read', 'Append', 'AppendTo'];
  if (bundle === 'crud') return ['Create', 'Read', 'Write', 'Delete'];
  // Everything except Delete — for tables whose deletes are BFF-mediated.
  if (bundle === 'crwaa') return ['Create', 'Read', 'Write', 'Append', 'AppendTo'];
  return ['Create', 'Read', 'Write', 'Delete', 'Append', 'AppendTo'];
}

// Standard CRM entities: privilege names use the schema name (PascalCase) — e.g.
// prvReadAccount, prvCreateOpportunityProduct. Custom (vba_*) entities use the
// logical name (compare case-insensitively: some custom tables carry PascalCase
// schema names like vba_ServiceOption).
const STANDARD_SCHEMA: Record<string, string> = {
  account: 'Account',
  contact: 'Contact',
  opportunity: 'Opportunity',
  opportunityproduct: 'OpportunityProduct',
  activitypointer: 'ActivityPointer',
  annotation: 'Annotation',
  task: 'Task',
  team: 'Team',
  systemuser: 'User',
  role: 'Role',
  privilege: 'Privilege',
};

export function privName(action: PrivilegeAction, logicalName: string): string {
  const entityPart = logicalName.includes('_')
    ? logicalName
    : STANDARD_SCHEMA[logicalName] ?? logicalName.charAt(0).toUpperCase() + logicalName.slice(1);
  return `prv${action}${entityPart}`;
}

/** Every privilege name a role should carry, per the matrix. Compare against
 *  the environment's privilege catalog case-insensitively. */
export function privilegesNeededForRole(roleKey: RoleKey): string[] {
  const out: string[] = [];
  for (const t of ALL_ACCESS) {
    const bundle = t[roleKey];
    if (!bundle) continue;
    for (const action of actionsForBundle(bundle)) {
      out.push(privName(action, t.logicalName));
    }
  }
  return out;
}
